import React, { useState, useRef, useEffect } from 'react';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import { connect } from 'react-redux';
import Modal from '$/components/global/modal';
import InputBlock from '$/components/global/inputBlock';
import { Formik, Field, ErrorMessage, FormikProps } from 'formik';
import * as Yup from 'yup';
import BixButton from '$/components/global/bixButton';
import { createClo } from '$/store/clo/actions';
import DatePickerField from '$/components/datePickerField';
import { Navigate } from 'react-router-dom';

export interface ICreateCloProps {
  cloIsLoading: boolean;
  error?: Bixby.Models.BixbyError;
  currentClo?: Bixby.Models.Clo;
  isCreatingClo: boolean;
  onCloseModal: () => void;
  dispatchCreateClo: (newClo: Bixby.Models.CreateCloRequest) => Promise<void>;
}

export interface CreateCloValues {
  name: string;
  fitchDealId: string;
  bixbyCloId: string;
  manager: string;
  closingDate: Date;
}

const CreateClo: React.FC<ICreateCloProps> = props => {
  const { currentClo, isCreatingClo, onCloseModal, cloIsLoading, dispatchCreateClo } = props;
  const [redirectUrl, setRedirectUrl] = useState('');

  useEffect(() => {
    if (currentClo && !Bixby.Utils.isNullOrEmpty(currentClo)) {
      setRedirectUrl(`/admin/clo/${currentClo.id}`);
    }
  }, [currentClo]);

  const initialNewClo: Bixby.Models.CreateCloRequest = {
    name: '',
    fitchDealId: '',
    bixbyCloId: '',
    manager: '',
    closingDate: new Date(),
  };

  const formikRef = useRef<FormikProps<CreateCloValues>>(null);

  const handleCloseModal = () => {
    if (formikRef && formikRef.current) {
      formikRef.current.resetForm();
    }
    onCloseModal();
  };

  const normalRequiredField = Yup.string().max(256, 'Too long').required();

  const schema = Yup.object({
    name: normalRequiredField.required('Clo name is required.'),
    fitchDealId: normalRequiredField.required('Clo Id is required.'),
    bixbyCloId: Yup.string().required('Bixby Clo Id is required.'),
  });

  const createCloForm = (
    <Modal title="Add New Clo Profile" onClose={handleCloseModal} active={isCreatingClo}>
      <div>
        <Formik
          innerRef={formikRef}
          initialValues={initialNewClo}
          validationSchema={schema}
          onSubmit={async (values: CreateCloValues) => {
            const newClo: Bixby.Models.CreateCloRequest = values;
            await dispatchCreateClo(newClo);
            handleCloseModal();
          }}
          render={({ handleSubmit, values, setFieldValue }) => (
            <form className="create-company-form" onSubmit={handleSubmit}>
              <div className="profile-input-container">
                <div>
                  <InputBlock title="Clo Name">
                    <Field type="text" name="name" />
                  </InputBlock>
                  <ErrorMessage name="name">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
                  <InputBlock title="CLO Id">
                    <Field type="text" name="fitchDealId"></Field>
                  </InputBlock>
                  <ErrorMessage name="fitchDealId">
                    {msg => <div className="error error-message">{msg}</div>}
                  </ErrorMessage>
                  <InputBlock title="Bixby CLO Id">
                    <Field type="text" name="bixbyCloId"></Field>
                  </InputBlock>
                  <ErrorMessage name="bixbyCloId">
                    {msg => <div className="error error-message">{msg}</div>}
                  </ErrorMessage>
                  <InputBlock title="Manager">
                    <Field type="text" name="manager" />
                  </InputBlock>
                  <InputBlock title="Closing Date">
                    <DatePickerField
                      name="closingDate"
                      value={values.closingDate}
                      onChange={(_field: string, value: Date) => {
                        if (!value) {
                          setFieldValue('closingDate', undefined);
                        }
                        setFieldValue('closingDate', value);
                      }}
                      isRestrictBackdate={true}
                      dateFormat="MMMM d, yyyy"
                    />
                  </InputBlock>
                </div>
              </div>
              <BixButton useSmall type="submit" disabled={cloIsLoading} useLoading isLoading={cloIsLoading}>
                Add to Portal
              </BixButton>
            </form>
          )}></Formik>
      </div>
    </Modal>
  );

  return redirectUrl && currentClo ? <Navigate to={redirectUrl} /> : <div>{createCloForm}</div>;
};

const mapStateToProps = (state: AppState) => {
  return {
    cloIsLoading: state.cloState.isLoading,
    error: state.cloState.error,
    currentClo: state.cloState.currentClo,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchCreateClo: (newClo: Bixby.Models.CreateCloRequest) => dispatch(createClo(newClo)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateClo);
